/**
 * Case Profile card — case-posture inputs the SpatchVenire model needs beyond
 * the juror questionnaire: the evidence-strength master gate and pretrial-
 * publicity level. Persists to state.caseProfile and feeds scoreJurorSV.
 * Victim and defendant facts live on the Parties tab (see features/parties.ts):
 * a case may have several victims but only one defendant.
 *
 * No capital-case field here — see the note on CaseProfile in types/state.ts:
 * MA state courts have no death penalty, so it would almost never apply.
 */

import type { EvidenceStrength, PretrialPublicity } from '@/types';
import { store } from '@/app/store';

type FieldValue<F extends 'evidence' | 'pretrialPublicity'> = F extends 'evidence'
  ? EvidenceStrength
  : PretrialPublicity;

function pillGroup<F extends 'evidence' | 'pretrialPublicity'>(
  field: F,
  current: string,
  levels: FieldValue<F>[]
): string {
  return levels
    .map((lv) => {
      const on = lv === current;
      return (
        `<button onclick="setCaseProfileField('${field}', '${lv}')" style="border:1px solid ${on ? '#6366f1' : '#cbd5e1'};` +
        `background:${on ? '#6366f1' : '#fff'};color:${on ? '#fff' : '#475569'};border-radius:6px;padding:4px 12px;` +
        `font-size:11px;font-weight:700;cursor:pointer;text-transform:capitalize;">${lv}</button>`
      );
    })
    .join(' ');
}

/** Render the Case Profile card into #case-profile-card from current state. */
export function renderCaseProfile(): void {
  const host = document.getElementById('case-profile-card');
  if (!host) return;
  const p = store.getState().caseProfile;
  const evidenceBtns = pillGroup('evidence', p.evidence, ['weak', 'close', 'strong']);
  const publicityBtns = pillGroup('pretrialPublicity', p.pretrialPublicity, ['low', 'moderate', 'high']);
  host.innerHTML =
    '<div class="card">' +
    '<div class="card-title"><div class="cticon"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" stroke-width="2.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg></div>Case Profile (Go/No-Go inputs)</div>' +
    '<p style="font-size:11px;color:var(--text-secondary);margin-bottom:9px;">Counsel-rated case posture — the SpatchVenire model\'s master gate and publicity/venue screens. Revisable any time.</p>' +
    `<div class="fg"><label>Evidence strength</label><div style="display:flex;gap:7px;">${evidenceBtns}</div></div>` +
    `<div class="fg" style="margin-top:9px;"><label>Pretrial publicity</label><div style="display:flex;gap:7px;">${publicityBtns}</div></div>` +
    '<p style="font-size:10px;color:var(--text-muted);margin-top:6px;">High publicity adds a per-juror recall-detail probe (Steblay 1999/2006) to the voir-dire capture below.</p>' +
    `<label style="display:flex;align-items:center;gap:8px;font-size:12px;margin-top:10px;"><input type="checkbox" ${p.localCommunityImpact ? 'checked' : ''} onchange="setCaseProfileField('localCommunityImpact', this.checked)" style="width:15px;height:15px;"> This crime harmed the local community (local victim, local fear, damaged local institution)</label>` +
    '<p style="font-size:10px;color:var(--text-muted);margin-top:4px;">Adds a per-juror residence/connection screen — MA\'s compact counties often create dense degrees-of-separation ties (Tsarnaev, 595 U.S. 302 (2022); Steblay et al. 1999).</p>' +
    '<p style="font-size:10px;color:var(--text-muted);margin-top:8px;">Victim and defendant facts (age, LE status, relationship, officer/corporate status, etc.) are recorded on the <strong>Parties</strong> tab — supports several victims and one defendant.</p>' +
    '</div>';
}

/** Update one case-profile field and persist. */
export function setCaseProfileField(
  field: 'evidence' | 'pretrialPublicity' | 'localCommunityImpact',
  value: string | boolean
): void {
  const st = store.getState();
  st.caseProfile = { ...st.caseProfile, [field]: value };
  store.save();
  renderCaseProfile();
}
